import { RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { Message, Profile } from '../types';
import { getSupabase } from '../lib/supabase';
import { formatTimestamp } from '../lib/format';
import Avatar from './Avatar';

interface AdminStats {
  users: number;
  conversations: number;
  messages: number;
  friendships: number;
}

export default function AdminDashboard({ currentProfile }: { currentProfile: Profile }) {
  const [stats, setStats] = useState<AdminStats>({ users: 0, conversations: 0, messages: 0, friendships: 0 });
  const [users, setUsers] = useState<Profile[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    const client = getSupabase();
    setLoading(true);
    setError(null);

    const [profilesCount, conversationsCount, messagesCount, friendshipsCount, profileRows, messageRows] =
      await Promise.all([
        client.from('profiles').select('*', { count: 'exact', head: true }),
        client.from('conversations').select('*', { count: 'exact', head: true }),
        client.from('messages').select('*', { count: 'exact', head: true }),
        client.from('friendships').select('*', { count: 'exact', head: true }),
        client.from('profiles').select('*').order('created_at', { ascending: false }).limit(8),
        client.from('messages').select('*').order('created_at', { ascending: false }).limit(8),
      ]);

    const firstError =
      profilesCount.error ||
      conversationsCount.error ||
      messagesCount.error ||
      friendshipsCount.error ||
      profileRows.error ||
      messageRows.error;

    if (firstError) {
      setError(firstError.message);
    } else {
      setStats({
        users: profilesCount.count ?? 0,
        conversations: conversationsCount.count ?? 0,
        messages: messagesCount.count ?? 0,
        friendships: friendshipsCount.count ?? 0,
      });
      setUsers((profileRows.data ?? []) as Profile[]);
      setMessages((messageRows.data ?? []) as Message[]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  async function softDeleteMessage(messageId: string) {
    const client = getSupabase();
    const { error: deleteError } = await client
      .from('messages')
      .update({ deleted_at: new Date().toISOString(), content: null, attachment_path: null })
      .eq('id', messageId);

    if (deleteError) setError(deleteError.message);
    await loadDashboard();
  }

  if (currentProfile.role !== 'admin') {
    return (
      <main className="grid h-screen flex-1 place-items-center bg-ink-850 p-6 text-center">
        <div>
          <ShieldCheck className="mx-auto mb-4 text-slate-600" size={42} />
          <h1 className="text-xl font-bold text-white">Admin access required</h1>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-400">
            Set your profile role to admin in Supabase to unlock moderation tools.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="relay-scrollbar h-screen flex-1 overflow-y-auto bg-ink-850 p-4 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin dashboard</h1>
            <p className="mt-1 text-sm text-slate-400">Monitor users, conversations, messages, and friend activity.</p>
          </div>
          <button
            type="button"
            onClick={loadDashboard}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {error && <p className="rounded-lg border border-relay-red/30 bg-relay-red/10 p-3 text-sm text-relay-red">{error}</p>}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['Users', stats.users],
            ['Conversations', stats.conversations],
            ['Messages', stats.messages],
            ['Friendships', stats.friendships],
          ].map(([label, value]) => (
            <section key={label} className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
              <p className="text-sm text-slate-500">{label}</p>
              <p className="mt-2 text-3xl font-bold text-white">{value}</p>
            </section>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <section className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Newest users</h2>
            <div className="divide-y divide-white/10">
              {users.map((user) => (
                <div key={user.id} className="flex items-center gap-3 py-3">
                  <Avatar name={user.display_name} src={user.avatar_url} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{user.display_name}</p>
                    <p className="truncate text-xs text-slate-500">
                      @{user.username} · {user.role}
                    </p>
                  </div>
                  <span className="text-xs text-slate-600">{formatTimestamp(user.created_at)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Recent messages</h2>
            <div className="divide-y divide-white/10">
              {messages.map((message) => (
                <div key={message.id} className="flex items-start gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">
                      {message.deleted_at ? 'Deleted message' : message.content || message.attachment_name || 'Attachment'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {message.kind} · {formatTimestamp(message.created_at)}
                    </p>
                  </div>
                  {!message.deleted_at && (
                    <button
                      type="button"
                      onClick={() => softDeleteMessage(message.id)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition hover:bg-relay-red/15 hover:text-relay-red"
                      aria-label="Delete message"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

